import { KeyRound, ShieldCheck } from 'lucide-react';
import { useState, type FormEvent } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { BrandMark } from '@/components/Brand';
import { Button, FormField, Input } from '@/components/ui';
import { brand } from '@/config/brand';
import { useAuth } from '@/hooks/useAuth';
import { useT } from '@/i18n';
import { ApiError } from '@/lib/api';
import { paths } from '@/router/paths';

export function LoginPage() {
  const { login, register } = useAuth();
  const navigate = useNavigate();
  const t = useT();

  const [mode, setMode] = useState<'login' | 'register'>('login');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [name, setName] = useState('');
  const [totpCode, setTotpCode] = useState('');
  const [totpRequired, setTotpRequired] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [verificationSent, setVerificationSent] = useState(false);
  const [busy, setBusy] = useState(false);

  const submit = async (event: FormEvent) => {
    event.preventDefault();
    setError(null);
    setBusy(true);

    try {
      if (mode === 'login') {
        await login(email, password, totpRequired ? totpCode : undefined);
      } else {
        const result = await register(email, password, name || undefined);
        if (result.verificationRequired) {
          // Konto angelegt, aber noch keine Sitzung: erst den Link aus der
          // Mail klicken. Hier bleiben und das erklaeren statt zu navigieren.
          setVerificationSent(true);
          setMode('login');
          setPassword('');
          return;
        }
      }
      navigate(paths.dashboard);
    } catch (cause) {
      if (cause instanceof ApiError && cause.code === 'TOTP_REQUIRED') {
        // Zweiten Faktor nachfordern, ohne die Eingaben zu verlieren
        setTotpRequired(true);
        setError(null);
      } else {
        setError(cause instanceof Error ? cause.message : t('auth.failed'));
      }
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="relative flex min-h-screen items-center justify-center overflow-hidden bg-bg-0 px-4 py-10">
      {/* Farbschimmer im Hintergrund */}
      <div
        className="pointer-events-none absolute inset-0"
        style={{
          background:
            'radial-gradient(70% 55% at 50% -10%, var(--acc-soft), transparent 70%), radial-gradient(50% 40% at 90% 100%, var(--info-soft), transparent 70%)',
        }}
        aria-hidden
      />

      <div className="relative w-full max-w-[420px]">
        <div className="mb-7 flex flex-col items-center gap-3.5 text-center">
          <BrandMark size={52} />
          <div>
            <h1 className="text-[22px] font-extrabold text-t0">{brand.name}</h1>
            <p className="mt-1 text-[13px] text-t1">
              {mode === 'login' ? t('auth.signInTitle') : t('auth.signUpTitle')}
            </p>
          </div>
        </div>

        <form
          onSubmit={submit}
          className="rounded-[var(--radius-vela-xl)] border border-line bg-bg-2 p-6 shadow-[var(--shadow-vela)]"
        >
          <div className="space-y-4">
            <FormField label={t('auth.email')} required>
              <Input
                type="email"
                autoComplete="email"
                required
                value={email}
                onChange={(event) => setEmail(event.target.value)}
                placeholder="name@firma.de"
              />
            </FormField>

            {mode === 'register' && (
              <FormField label={t('auth.name')} hint={t('auth.nameHint')}>
                <Input
                  type="text"
                  autoComplete="name"
                  value={name}
                  onChange={(event) => setName(event.target.value)}
                />
              </FormField>
            )}

            <FormField
              label={t('auth.password')}
              required
              hint={mode === 'register' ? t('auth.passwordHint') : undefined}
            >
              <Input
                type="password"
                autoComplete={mode === 'login' ? 'current-password' : 'new-password'}
                required
                minLength={mode === 'register' ? 10 : 8}
                value={password}
                onChange={(event) => setPassword(event.target.value)}
              />
            </FormField>

            {mode === 'login' && (
              <p className="-mt-1 text-right">
                <Link
                  to={paths.passwordForgot}
                  className="text-[12px] text-t2 transition hover:text-acc"
                >
                  {t('auth.forgotPassword')}
                </Link>
              </p>
            )}

            {totpRequired && (
              <FormField label={t('auth.totpCode')} required hint={t('auth.totpHint')}>
                <Input
                  inputMode="numeric"
                  autoComplete="one-time-code"
                  required
                  autoFocus
                  className="font-mono tracking-[0.3em]"
                  placeholder="000000"
                  value={totpCode}
                  onChange={(event) => setTotpCode(event.target.value)}
                />
              </FormField>
            )}

            {verificationSent && !error && (
              <div className="rounded-[var(--radius-vela-md)] border border-line bg-ok-soft px-3 py-2.5 text-[13px] font-medium text-ok">
                {t('auth.registerVerifySent')}
              </div>
            )}
            {error && (
              <div className="rounded-[var(--radius-vela-md)] border border-line bg-bad-soft px-3 py-2.5 text-[13px] font-medium text-bad">
                {error}
              </div>
            )}

            <Button type="submit" size="lg" fullWidth disabled={busy} icon={<KeyRound size={16} />}>
              {mode === 'login' ? t('auth.signIn') : t('auth.signUp')}
            </Button>
          </div>

          <p className="mt-5 text-center text-[13px] text-t1">
            {mode === 'login' ? t('auth.noAccount') : t('auth.hasAccount')}{' '}
            <button
              type="button"
              className="font-bold text-acc hover:text-acc-2"
              onClick={() => {
                setMode(mode === 'login' ? 'register' : 'login');
                setError(null);
                setTotpRequired(false);
              }}
            >
              {mode === 'login' ? t('auth.toSignUp') : t('auth.toSignIn')}
            </button>
          </p>
        </form>

        <p className="mt-5 flex items-center justify-center gap-1.5 text-[11.5px] text-t2">
          <ShieldCheck size={13} />
          {t('auth.secured')}
        </p>
      </div>
    </div>
  );
}
